"use client";

import { useState } from "react";
import { TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  TABLE as SPECIES_TABLE,
  type TableSpecies,
} from "@/lib/tool-kit/chem-reference/reaction-enthalpy";

export interface EnthalpyTableStrings {
  tableHeading: string;
  filterLabel: string;
  filterPlaceholder: string;
  columnSpecies: string;
  columnName: string;
  columnDhf: string;
  columnAdd: string;
  addReactant: string;
  addProduct: string;
  addReactantLabel: string;
  addProductLabel: string;
  noSpecies: string;
  tableSource: string;
}

function matches(species: TableSpecies, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") {
    return true;
  }
  return (
    species.label.toLowerCase().includes(q) ||
    species.raw.toLowerCase().includes(q) ||
    species.name.toLowerCase().includes(q)
  );
}

/** Every tabulated species with its ΔfH°, filterable, with buttons to add it to the equation. */
export function EnthalpyTable({
  strings,
  fmt,
  onAdd,
}: {
  strings: EnthalpyTableStrings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  onAdd: (species: TableSpecies, side: "reactants" | "products") => void;
}) {
  const [query, setQuery] = useState("");
  const rows = SPECIES_TABLE.filter((species) => matches(species, query));
  return (
    <div className="space-y-3">
      <TextField
        id="er-filter"
        label={strings.filterLabel}
        onChange={setQuery}
        placeholder={strings.filterPlaceholder}
        type="search"
        value={query}
      />
      <div className="max-h-96 overflow-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {strings.tableHeading}
          </caption>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.columnSpecies}
              </th>
              <th className={TH} scope="col">
                {strings.columnName}
              </th>
              <th className={TH} scope="col">
                {strings.columnDhf}
              </th>
              <th className={TH} scope="col">
                {strings.columnAdd}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((species) => (
              <tr key={species.label}>
                <td className={`${TD} font-mono`}>{species.label}</td>
                <td className={TD}>{species.name}</td>
                <td className={TD}>
                  {fmt(species.dHf, {
                    maximumFractionDigits: 2,
                    useGrouping: false,
                  })}
                </td>
                <td className={TD}>
                  <span className="flex gap-2">
                    <button
                      aria-label={t(strings.addReactantLabel, {
                        species: species.label,
                      })}
                      className={BUTTON_SMALL}
                      onClick={() => onAdd(species, "reactants")}
                      type="button"
                    >
                      {strings.addReactant}
                    </button>
                    <button
                      aria-label={t(strings.addProductLabel, {
                        species: species.label,
                      })}
                      className={BUTTON_SMALL}
                      onClick={() => onAdd(species, "products")}
                      type="button"
                    >
                      {strings.addProduct}
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <p className="p-3 text-muted-foreground text-sm">
            {strings.noSpecies}
          </p>
        ) : null}
      </div>
      <p className="text-muted-foreground text-xs">{strings.tableSource}</p>
    </div>
  );
}
